import { TaInput } from '@components/shared';
import { Dictionary } from '@utils/util.types';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteField } from './AutocompleteField';

export const CropAreaAndUnit = ({ 
	formik, 
	cropUnitOptions: propCropUnitOptions 
}: { 
	formik: any;
	cropUnitOptions?: Dictionary[];
}) => {
	const { t } = useTranslation();
	const hasSetDefaultRef = useRef(false);

	// Use provided options or fallback to default
	const cropUnitOptions: Dictionary[] = propCropUnitOptions || [
		{ name: t('unitTypes.m2'), id: 'm2' },
		{ name: t('unitTypes.hectares'), id: 'hectares' },
		{ name: t('unitTypes.acres'), id: 'acres' },
	];

	// Define default option
	const defaultUnitOption = useMemo(() => {
		return cropUnitOptions.find(option => option.id === 'hectares') || cropUnitOptions[0];
	}, [cropUnitOptions]);

	// Ensure crop_unit is properly set with a valid option - ONLY ON MOUNT
	useEffect(() => {
		// Only run this once on mount
		if (hasSetDefaultRef.current) {
			return;
		}
		
		// Check if crop_unit is undefined or has an invalid name
		if (!formik.values.crop_unit || 
			(typeof formik.values.crop_unit === 'string' && formik.values.crop_unit === 'undefined') ||
			(typeof formik.values.crop_unit === 'object' && formik.values.crop_unit?.name === 'undefined') || 
			(typeof formik.values.crop_unit === 'object' && formik.values.crop_unit?.name === 'HA')) {
			
			console.log('Invalid crop unit detected:', formik.values.crop_unit);
			console.log('Setting default crop unit to hectares');
			
			const hectaresOption = cropUnitOptions.find(option => option.id === 'hectares');
			formik.setFieldValue('crop_unit', hectaresOption);
			
			console.log('Set crop_unit to:', hectaresOption);
		} else {
			// If we have a string instead of an object, find the matching option
			if (typeof formik.values.crop_unit === 'string') {
				console.log('String crop unit detected:', formik.values.crop_unit);
				
				// Try to find by id or name
				let unitOption = cropUnitOptions.find(option => 
					option.id === formik.values.crop_unit || 
					option.name === formik.values.crop_unit
				);
				
				if (unitOption) {
					console.log('Found matching unit option:', unitOption);
					formik.setFieldValue('crop_unit', unitOption);
				} else {
					console.log('No matching unit found, using default');
					formik.setFieldValue('crop_unit', cropUnitOptions.find(option => option.id === 'hectares'));
				}
			}
		}
		
		// Mark that we've set the default
		hasSetDefaultRef.current = true;
	}, [cropUnitOptions, formik.setFieldValue]); // Remove formik.values.crop_unit dependency

	return (
		<div className="flex flex-col sm:flex-row gap-4 w-full">
			<div className="w-full sm:w-[70%]">
				<TaInput
					id="crop_area"
					label={t('myFarm.crop_area')}
					size="small"
					type="text"
					isRequired
					{...formik.getFieldProps('crop_area')}
					error={formik.errors.crop_area}
					touched={formik.touched.crop_area}
				/>
			</div>
			<div className="w-full sm:w-[30%]">
				<AutocompleteField
					formik={formik}
					name="crop_unit"
					label={t('unit')}
					options={cropUnitOptions}
					icon={<div />}
					defaultOption={defaultUnitOption}
				/>
			</div>
		</div>
	);
};
